import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { ChevronRight } from 'react-bootstrap-icons';
import StorageAddForm from './StorageAddForm';
import StorageNodeActions, { StorageNodeRemoveButton } from './StorageNodeActions';
import StorageNodeIcon from './StorageNodeIcon';
import { logInfo } from '../../config_and_helpers/logger';
import {
  buildStorageColumns,
  canHaveChildren,
  countChildren,
  countLotsInside,
  formatStorageNodeCountLabel,
  getStorageNodeTypeLabel,
} from '../../utils/storageTree';
import './StorageExplorerStyle.css';

const LOG_CONTEXT = 'StorageNodeColumns';

/**
 * One place in a column. Clicking it selects that place, which fills the column
 * to its right with what is inside. Looking inside is the toolbar's Open.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode} props.node
 * @param {boolean} props.selected
 * @param {number} props.childCount
 * @param {number|null} props.lotCount
 * @param {Function} props.onSelect - (node) => void
 * @param {Function} [props.onPreviewPlace]
 * @param {Function} [props.onCancelPreview]
 */
const StorageNodeColumnRow = ({
  node,
  selected,
  childCount,
  lotCount,
  onSelect,
  onPreviewPlace,
  onCancelPreview,
}) => {
  const className = [
    'storage-columns__row',
    selected ? 'storage-columns__row--selected' : '',
    canHaveChildren(node) ? '' : 'storage-columns__row--leaf',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li>
      <button
        type="button"
        className={className}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onSelect?.(node)}
        onMouseEnter={() => onPreviewPlace?.(node)}
        onMouseLeave={() => onCancelPreview?.(node)}
      >
        <StorageNodeIcon node={node} className="storage-columns__icon" />
        <span className="storage-columns__text">
          <span className="storage-columns__name">{node.name}</span>
          <span className="storage-columns__meta">
            {formatStorageNodeCountLabel({ node, childCount, lotCount })}
          </span>
        </span>
        {canHaveChildren(node) ? (
          <ChevronRight className="storage-columns__chevron" aria-hidden="true" />
        ) : null}
      </button>
    </li>
  );
};

/**
 * The storage tree as columns running left to right, the third view at
 * `/storage`.
 *
 * The first column is the lab's rooms. Clicking a room fills the next column
 * with its storage units, then its shelves, drawers, and racks, then its boxes.
 * The hierarchy is bounded at four levels, so the columns are too. A click
 * selects and drills in — it does not open a modal over the columns.
 *
 * Everything you can do to the selected place is in one toolbar under the
 * columns, through the shared `StorageNodeActions`, so this view cannot drift
 * from the map card or the list row. **Open** there shows what is inside, using
 * the same contents modal and slot chart as the other views.
 *
 * The columns are derived from `selectedId` alone, so this view keeps no state
 * of its own and the selected place carries across all three views.
 *
 * @param {Object} props - Same place callbacks as `StorageMap` and `StorageNodeList`
 * @param {import('../../utils/storageTree').StorageNode[]} props.nodes
 * @param {string|null} [props.selectedId]
 * @param {Function} [props.onSelect] - (nodeId|null) => void
 * @param {Function} [props.onOpenNode] - (node) => void. Contents or the slot chart
 * @param {Object<string, number>|null} [props.lotCounts]
 */
const StorageNodeColumns = ({
  nodes = [],
  selectedId = null,
  onSelect,
  onAdd,
  onRemove,
  onDeleted,
  onOpenNode,
  onOpenBox,
  onEditBox,
  onMove,
  onRename,
  onPlaceItem,
  onPreviewPlace,
  onCancelPreview,
  lotCounts = null,
}) => {
  const [addingRoom, setAddingRoom] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const lastColumnRef = useRef(null);

  const columns = useMemo(() => buildStorageColumns(nodes, selectedId), [nodes, selectedId]);

  const selected = useMemo(() => {
    for (let index = columns.length - 1; index >= 0; index -= 1) {
      const column = columns[index];
      if (!column.selectedId) continue;
      return column.nodes.find(node => String(node.id) === column.selectedId) || null;
    }
    return null;
  }, [columns]);

  const rowCounts = useMemo(() => {
    const counts = new Map();
    columns.forEach(column => {
      column.nodes.forEach(node => {
        counts.set(String(node.id), {
          childCount: countChildren(nodes, node.id),
          lotCount: lotCounts ? countLotsInside(nodes, node.id, lotCounts) : null,
        });
      });
    });
    return counts;
  }, [columns, nodes, lotCounts]);

  const handleSelect = useCallback(
    node => {
      if (!node) return;
      logInfo(LOG_CONTEXT, 'storage column row selected', {
        nodeId: node.id,
        kind: node.kind,
        canHoldMore: canHaveChildren(node),
      });
      onSelect?.(node.id);
    },
    [onSelect]
  );

  const handleOpen = useCallback(() => {
    if (!selected) return;
    logInfo(LOG_CONTEXT, 'storage column place opened', {
      nodeId: selected.id,
      kind: selected.kind,
    });
    onOpenNode?.(selected);
  }, [onOpenNode, selected]);

  // Drilling deeper pushes a column off the right edge; bring it into view.
  useEffect(() => {
    const element = lastColumnRef.current;
    if (!element) return;
    if (typeof element.scrollIntoView !== 'function') return;
    element.scrollIntoView({ inline: 'end', block: 'nearest' });
  }, [columns.length]);

  return (
    <div className="storage-columns-view">
      <div className="storage-columns">
        {columns.map((column, index) => (
          <div
            className="storage-columns__col"
            key={column.parentId ?? 'root'}
            ref={index === columns.length - 1 ? lastColumnRef : null}
          >
            <div className="storage-columns__head">
              <span className="storage-columns__label">{column.label}</span>
              {column.parent ? (
                <span className="storage-columns__in">in {column.parent.name}</span>
              ) : null}
            </div>
            {column.nodes.length ? (
              <ul className="storage-columns__rows">
                {column.nodes.map(node => {
                  const counts = rowCounts.get(String(node.id)) || {
                    childCount: 0,
                    lotCount: null,
                  };
                  return (
                    <StorageNodeColumnRow
                      key={node.id}
                      node={node}
                      selected={column.selectedId === String(node.id)}
                      childCount={counts.childCount}
                      lotCount={counts.lotCount}
                      onSelect={handleSelect}
                      onPreviewPlace={onPreviewPlace}
                      onCancelPreview={onCancelPreview}
                    />
                  );
                })}
              </ul>
            ) : (
              <p className="storage-columns__empty mb-0">
                {column.parent
                  ? 'Nothing inside yet.'
                  : 'No rooms yet. Add the first one to start the map.'}
              </p>
            )}
          </div>
        ))}
      </div>

      <div
        className={[
          'storage-columns__toolbar',
          formOpen || addingRoom ? 'storage-columns__toolbar--form-open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {selected ? (
          <>
            <span className="storage-columns__selected">
              <StorageNodeIcon node={selected} className="storage-columns__icon" />
              <span className="storage-columns__name">{selected.name}</span>
              <span className="storage-columns__meta">
                {getStorageNodeTypeLabel(selected)}
              </span>
            </span>
            <Button
              className="glass-primary-btn storage-columns__action"
              size="sm"
              onClick={handleOpen}
            >
              Open
            </Button>
            <StorageNodeActions
              node={selected}
              nodes={nodes}
              onAdd={onAdd}
              onOpenBox={onOpenBox}
              onEditBox={onEditBox}
              onMove={onMove}
              onRename={onRename}
              onPlaceItem={onPlaceItem}
              onFormOpenChange={setFormOpen}
              buttonClassName="storage-columns__action"
              addButtonClassName="storage-columns__action"
              addFormIdPrefix={`columns-${selected.id}`}
              showOpenChart={false}
            />
            {onRemove ? (
              <StorageNodeRemoveButton
                node={selected}
                childCount={countChildren(nodes, selected.id)}
                onRemove={onRemove}
                onDeleted={onDeleted}
                className="storage-columns__remove"
              />
            ) : null}
          </>
        ) : typeof onAdd === 'function' && addingRoom ? (
          <>
            <StorageAddForm
              parent={null}
              compact
              idPrefix="columns-add-room"
              onAdd={async payload => {
                const ok = await onAdd?.(null, payload);
                if (ok !== false) setAddingRoom(false);
                return ok;
              }}
            />
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => setAddingRoom(false)}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <span className="storage-columns__hint">
              Pick a place to work on it.
            </span>
            {typeof onAdd === 'function' ? (
            <Button
              className="glass-primary-btn storage-columns__action"
              size="sm"
              onClick={() => {
                logInfo(LOG_CONTEXT, 'storage columns add room opened', {});
                setAddingRoom(true);
              }}
            >
              + Add room
            </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default StorageNodeColumns;
